/* eslint-disable max-params */
import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { UtilsService } from '@shared/services/utils.service';
import {
  IAnswer,
  IAnswerSchema,
  IModule,
  IQuestion,
  MarkStatus,
  Questionnaire,
  SMEQC,
} from '../models';
import {
  createCompositeKey,
  createCompositeKeyFromAnswer,
  DerivedModule,
  DerivedQuestionnaireState,
  getAllQuestionnaireQuestions,
  handleDuplicates,
} from '../questionnaire-utils';

import { cloneDeep } from 'lodash';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  take,
  tap,
} from 'rxjs';

import environment from '../../../../environments/environment';
import {
  getSavingAnswerMark,
  getSavingModuleAnswerMarks,
  getSavingModuleMark,
  selectLoadingQuestionnaire,
  selectModuleAnswerCounts,
  selectModuleVisibility,
  selectNextModule,
  selectPreviousModule,
  selectQuestionDict,
  selectQuestionnaire,
  selectQuestionnaireState,
  selectQuestionVisibility,
  selectSectionOfQuestionnaire,
} from '../redux/questionnaire.selectors';
import {
  addQuestion,
  clearActiveSectionOfQuestionnaire,
  getSectionOfQuestionnaireByName,
  loadingQuestionnaire,
  markModule,
  markModuleSuccess,
  removeQuestion,
  saveAnswerMarkError,
  saveAnswerMarkSuccess,
  savingAnswerMark,
  savingModuleMark,
  savingModuleMarkError,
  setActiveSectionOfQuestionnaire,
  setAnswer,
  storeQuestionDict,
  storeQuestionnaire,
  updateAnswerThroughSocket,
} from '../redux/questionnaire.actions';
import { calculateTodoCount } from '../redux/questionnaire.reducer';
import { selectActiveSubmissionId } from '../redux/submission.selectors';
import { closeActiveThread } from '../redux/submission.actions';
import { CUSTOM_ERROR_HANDLING } from '@shared/http';

@Injectable()
export class QuestionnaireService {
  private readonly _store = inject(Store);
  private readonly _http = inject(HttpClient);
  private readonly _utils = inject(UtilsService);
  section$: Observable<IModule> = this._store
    .select(selectSectionOfQuestionnaire)
    .pipe(filter((questionnaire): questionnaire is IModule => !!questionnaire));

  private questionnaire$: Observable<Questionnaire> = this._store
    .select(selectQuestionnaire)
    .pipe(
      filter(
        (questionnaire): questionnaire is Questionnaire => !!questionnaire,
      ),
    );

  addQuestion(questionId: string, subModuleId: string) {
    this._store.dispatch(addQuestion({ questionId, subModuleId }));
  }

  getQuestionVisibility(questionId: string): Observable<boolean> {
    return this._store.select(selectQuestionVisibility(questionId));
  }

  getModuleAnswerCounts(moduleId: string): Observable<DerivedModule> {
    return this._store.select(selectModuleAnswerCounts(moduleId));
  }

  getModuleVisibility(moduleId: string): Observable<boolean> {
    return this._store.select(selectModuleVisibility(moduleId));
  }

  getQuestionnaireState(
    submissionId: string,
  ): Observable<DerivedQuestionnaireState> {
    return this._store.select(selectQuestionnaireState).pipe(
      filter(
        (questionnaireState): questionnaireState is DerivedQuestionnaireState =>
          !!questionnaireState,
      ),
      filter(
        (questionnaireState) =>
          questionnaireState.submissionId === submissionId,
      ),
    );
  }

  getSavingModuleMark(moduleId: string, mark: SMEQC): Observable<boolean> {
    return this._store.select(getSavingModuleMark(moduleId, mark));
  }

  getSavingAnswerMark(answerId: string, mark: SMEQC): Observable<boolean> {
    return this._store.select(getSavingAnswerMark(answerId, mark));
  }

  getSavingModuleAnswerMarks(
    moduleId: string,
    mark: SMEQC,
  ): Observable<{ [key: string]: boolean }> {
    return this._store.select(getSavingModuleAnswerMarks(moduleId, mark));
  }

  /**
   * Get a question from the questionnaire by submission ID and composite key
   * @param submissionId The submission ID
   * @param compositeKey The composite key for the question (created by createCompositeKey)
   * @returns
   */
  getQuestion(submissionId: string, compositeKey: string) {
    return this.getSubmissionQuestionnaire(submissionId).pipe(
      map((questionnaire) => {
        const questions = getAllQuestionnaireQuestions(questionnaire);

        return questions.find(
          (question) => createCompositeKey(question) === compositeKey,
        );
      }),
    );
  }

  getQuestionnaire(
    submissionId: string,
    questionnaireId: string,
  ): Observable<Questionnaire> {
    return combineLatest([
      this._store.select(selectQuestionnaire),
      this._store.select(selectLoadingQuestionnaire),
    ]).pipe(
      take(1),
      mergeMap(([questionnaire, loadingState]) => {
        const compositeKey = `${questionnaireId}-${submissionId}`;
        const loading = loadingState[compositeKey];

        if (
          (!questionnaire ||
            questionnaire.id !== questionnaireId ||
            questionnaire.submissionId !== submissionId) &&
          !loading
        ) {
          this.fetchQuestionnaire(submissionId, questionnaireId);
        }

        return this.questionnaire$.pipe(
          filter(
            (questionnaire) =>
              questionnaire.id === questionnaireId &&
              questionnaire.submissionId === submissionId,
          ),
        );
      }),
    );
  }

  getSubmissionQuestionnaire(submissionId: string) {
    return this._store.select(selectQuestionnaire).pipe(
      take(1),
      filter(
        (questionnaire): questionnaire is Questionnaire => !!questionnaire,
      ),
      filter((questionnaire) => questionnaire.submissionId === submissionId),
    );
  }

  setActiveSection(index: number): void {
    this._store.dispatch(setActiveSectionOfQuestionnaire({ index }));
  }

  getSectionByName(title: string) {
    this._store.dispatch(getSectionOfQuestionnaireByName({ title }));
  }

  clearActiveSection(): void {
    this._store.dispatch(clearActiveSectionOfQuestionnaire());
  }

  handleQuestionAnswer(answer: IAnswer | IAnswer[]) {
    const processAnswer = (ans: IAnswer, updateAnswer = true) => {
      if (!ans.question_id) {
        console.warn('Answer does not have a question_id');
        return;
      }

      this._store
        .select(selectQuestionnaireState)
        .pipe(take(1))
        .subscribe((state) => {
          const questionId = ans.question_id as string;
          const compositeKey = createCompositeKeyFromAnswer(ans);
          const retrievedKey =
            compositeKey && compositeKey.length > 32
              ? Object.keys(state.questions).find((key) =>
                  key.includes(compositeKey ?? questionId),
                )
              : compositeKey;
          const question = state.questions[retrievedKey ?? questionId];
          const workspaceId = state.submissionId as string;
          ans.workspace_id = workspaceId;

          if (question && question.answerId) {
            ans.id = question.answerId;
          }
          if (!updateAnswer) return;
          if (ans.id) {
            this.updateAnswer(ans);
          } else {
            this.saveAnswer(ans);
          }
        });
    };

    // Check if the input is an array or a single answer
    if (Array.isArray(answer)) {
      // If it's an array, process each answer individually
      answer.forEach((ans) => processAnswer(ans, false));
      this.updateAnswer(answer);
    } else {
      // Otherwise, process the single answer
      processAnswer(answer);
    }
  }

  updateQuestionAnswerTags(
    submissionId: string,
    answerId: string,
    tags: string[],
  ) {
    this._http
      .put<any>(
        `${environment.irisApi}/workspace/${submissionId}/answer/${answerId}/tag/`,
        tags,
      )
      .subscribe();
  }

  getPreviousModule(moduleId: string) {
    return this._store.select(selectPreviousModule(moduleId));
  }

  getNextModule(moduleId: string) {
    return this._store.select(selectNextModule(moduleId));
  }

  hasNextModule(moduleId: string) {
    return this._store
      .select(selectNextModule(moduleId))
      .pipe(map((nextModule) => !!nextModule));
  }

  hasPreviousModule(moduleId: string) {
    return this._store
      .select(selectPreviousModule(moduleId))
      .pipe(map((previousModule) => !!previousModule));
  }

  updateQuestionReviewStatus(
    markStatus: {
      id: string;
      qc_mark?: boolean;
      sme_mark?: boolean;
    },
    markType: SMEQC,
  ) {
    this._store.dispatch(
      savingAnswerMark({
        answerId: markStatus.id,
        mark: markType,
      }),
    );

    this._http
      .put<any>(`${environment.irisApi}/answer/mark/`, markStatus)
      .pipe(
        catchError(() => {
          this._store.dispatch(
            saveAnswerMarkError({ answerId: markStatus.id, mark: markType }),
          );
          return EMPTY;
        }),
      )
      .subscribe((answer: any) => {
        answer = this._utils.convertKeysToCamelCase(answer);
        this._store.dispatch(saveAnswerMarkSuccess({ answer, mark: markType }));
      });
  }

  removeDuplicateQuestion(
    submissionId: string,
    question: IQuestion,
    orderNum: number,
  ) {
    const answers =
      (question.children
        ?.map((child) => child.answer)
        .filter((a) => !!a) as IAnswer[]) ?? [];

    if (answers.length) {
      const apiCalls = answers?.map((answer) =>
        this._http.delete<any>(
          `${environment.irisApi}/workspace/${submissionId}/answer/${answer.id}/`,
        ),
      );

      forkJoin(apiCalls).subscribe(() => {
        this._store.dispatch(
          removeQuestion({
            questionId: question.id,
            subModuleId: question.moduleId,
            orderNumber: orderNum,
          }),
        );
      });
    } else {
      this._store.dispatch(
        removeQuestion({
          questionId: question.id,
          subModuleId: question.moduleId,
          orderNumber: orderNum,
        }),
      );
    }
  }

  updateSectionReviewStatus(
    module: IModule,
    workspaceId: string,
    markStatus: MarkStatus,
    markType: SMEQC,
  ) {
    this._store.dispatch(
      savingModuleMark({ moduleId: module.id, mark: markType }),
    );

    this._http
      .put<MarkStatus>(
        `${environment.irisApi}/module/${module.id}/mark/${workspaceId}/`,
        markStatus,
      )
      .pipe(
        catchError(() => {
          this._store.dispatch(
            savingModuleMarkError({ moduleId: module.id, mark: markType }),
          );
          return EMPTY;
        }),
      )
      .subscribe(() => {
        const updatedModule = cloneDeep(module);

        if (markStatus.qc_mark !== undefined) {
          updatedModule.qcMark = markStatus.qc_mark;
        }

        if (markStatus.sme_mark !== undefined) {
          updatedModule.smeMark = markStatus.sme_mark;
        }

        this._store.dispatch(
          markModule({
            module: updatedModule,
            status: markStatus,
            mark: markType,
          }),
        );
        this._store.dispatch(
          markModuleSuccess({
            module: updatedModule,
          }),
        );
      });
  }

  selectSectionFromThread(threadId: string) {
    this._store
      .select(selectQuestionDict)
      .pipe(
        take(1),
        map((questionDict: any) => {
          const entity = questionDict[threadId];
          if (entity) {
            return entity.data.sectionIndex;
          }
          return null;
        }),
      )
      .subscribe((sectionIndex) => {
        this._store.dispatch(
          setActiveSectionOfQuestionnaire({ index: sectionIndex }),
        );
      });
  }

  /**
   * Reload the questionnaire
   */
  reloadQuestionnaire(quietly = false) {
    this._store
      .select(selectQuestionnaire)
      .pipe(
        filter((q) => !!q),
        take(1),
      )
      .subscribe((q) => {
        if (q) {
          this.fetchQuestionnaire(q.submissionId, q.id, quietly);
        }
      });

    return of(null);
  }

  private fetchQuestionnaire(
    submissionId: string,
    questionnaireId: string,
    quietly = false,
  ) {
    if (!quietly) {
      this._store.dispatch(
        loadingQuestionnaire({
          compositeKey: `${questionnaireId}-${submissionId}`,
        }),
      );
    }

    this._http
      .get<Questionnaire>(
        `${environment.irisApi}/workspace/${submissionId}/questionnaire/`,
        {
          context: new HttpContext().set(CUSTOM_ERROR_HANDLING, 'silent-mode'),
        },
      )
      .pipe(
        map((questionnaire: Questionnaire) => {
          questionnaire = this._utils.convertKeysToCamelCase(questionnaire);
          questionnaire.modules = questionnaire.modules
            .map((module) => {
              // Sort the modules, submodules, questions, choices and children before further processing
              module.subModules = module.subModules
                ?.map((subModule) => {
                  subModule.questions = subModule.questions?.map((question) => {
                    question.choices = question.choices?.sort(
                      // Types are off in the choice interface
                      (a: any, b: any) => a.orderNum - b.orderNum,
                    );

                    question.children = question.children?.sort(
                      (a, b) => a.orderNum - b.orderNum,
                    );

                    return question;
                  });
                  return subModule;
                })
                .sort((a, b) => a.orderNum - b.orderNum);

              return module;
            })
            // Types are off in the module interface
            .sort((a: any, b: any) => a.orderNum - b.orderNum);

          return questionnaire;
        }),
        map((questionnaire: Questionnaire) => {
          questionnaire.modules = questionnaire.modules.map(
            (module: IModule, i: number) => ({
              ...module,
              index: i,
              moduleLength: questionnaire.modules.length,
            }),
          );

          // Store submissionId in questionnaire to check if the questionnaire is for the current submission
          questionnaire.submissionId = submissionId;

          return questionnaire;
        }),
        tap((questionnaire: Questionnaire) => {
          const entityToObj: {
            [key: string]: {
              title: string;
              type: string;
              data: { sectionIndex: number };
            };
          } = {};
          questionnaire = this._utils.convertKeysToCamelCase(questionnaire);

          questionnaire.modules.forEach((module) => {
            module.subModules.forEach((subModule) => {
              subModule.questions.forEach((question) => {
                if (question.answer) {
                  entityToObj[question.answer.id] = {
                    title: this._utils.stripHtml(question.title),
                    type: 'answer',
                    data: {
                      sectionIndex: module.index,
                    },
                  };
                }

                if (question.children && question.children.length > 0) {
                  question.duplicates = [];

                  const duplicates: {
                    [groupNum: number]: {
                      [questionId: string]: any;
                    };
                  } = {};

                  question.children.forEach((child) => {
                    if (child.answer) {
                      entityToObj[child.answer.id] = {
                        title: this._utils.stripHtml(child.title),
                        type: 'answer',
                        data: {
                          sectionIndex: module.index,
                        },
                      };
                    } else if (child.answers) {
                      child.answers.forEach((answer: any) => {
                        if (!duplicates[answer.groupNum]) {
                          duplicates[answer.groupNum] = {};
                        }

                        duplicates[answer.groupNum][answer.questionId] = answer;

                        entityToObj[answer.id] = {
                          title: this._utils.stripHtml(child.title),
                          type: 'answer',
                          data: {
                            sectionIndex: module.index,
                          },
                        };
                      });
                    }
                  });

                  const duplicateKeys = Object.keys(duplicates).sort(
                    (a, b) => parseInt(a) - parseInt(b),
                  );

                  const firstKey = duplicateKeys.shift();

                  if (firstKey) {
                    question.children.forEach((child) => {
                      child.answer = duplicates[parseInt(firstKey)][child.id];
                    });
                  }
                  //While mapping duplicates from the endpoint response, I'm checking the state on the frontend. If the state already exists, there's no need to map the duplicates again after receiving the questionnaire from the endpoint
                  this._store
                    .select(selectQuestionnaire)
                    .pipe(take(1))
                    .subscribe((questionnaire) => {
                      if (!questionnaire) {
                        //map duplicate from response at first time
                        question = handleDuplicates(
                          question,
                          duplicateKeys,
                          duplicates,
                        );
                        return;
                      }

                      const questions =
                        getAllQuestionnaireQuestions(questionnaire);

                      const stateQuestion = questions.find(
                        (q) => q.id === question.id,
                      );

                      //checking duplicates already exists in state and if exists just mapping from the state
                      if (
                        stateQuestion?.duplicates?.length &&
                        questionnaire?.submissionId === submissionId
                      ) {
                        question.duplicates = stateQuestion.duplicates;
                        return;
                      }

                      //This will map duplicates only when no duplicate information for the question exists in the state
                      question = handleDuplicates(
                        question,
                        duplicateKeys,
                        duplicates,
                      );
                    });
                } else if (question.answers) {
                  question.answers.map((answer) => {
                    entityToObj[answer.id] = {
                      title: this._utils.stripHtml(question.title),
                      type: 'answer',
                      data: {
                        sectionIndex: module.index,
                      },
                    };
                  });
                } else {
                  entityToObj[question.id] = {
                    title: this._utils.stripHtml(question.title),
                    type: 'question',
                    data: {
                      sectionIndex: module.index,
                    },
                  };
                }
              });
            });

            // Get the todo count after finding all the sub-modules
            module.toDo = calculateTodoCount(module);
          });

          this._store.dispatch(
            storeQuestionDict({ questionDict: entityToObj }),
          );
          this._store.dispatch(
            storeQuestionnaire({
              questionnaire,
            }),
          );
        }),

        catchError((err) => {
          console.error('Error fetching questionnaire:', err);
          return of(''); // Return an observable with an empty string as error state
        }),
      )
      .subscribe();
  }

  private saveAnswer(answer: IAnswer): void {
    this._store
      .select(selectActiveSubmissionId)
      .pipe(take(1))
      .subscribe((submissionId) => {
        answer.workspace_id = submissionId;
        this._http
          .post<IAnswer>(
            `${environment.irisApi}/workspace/${submissionId}/answer/`,
            answer,
          )
          .subscribe((response: IAnswer) => {
            this.updateAnswersAndThreads(response);
          });
      });
  }

  private updateAnswer(answer: IAnswer | IAnswer[]) {
    this._store
      .select(selectActiveSubmissionId)
      .pipe(take(1))
      .subscribe((submissionId) => {
        const processAnswer = (ans: IAnswer) => {
          ans.workspace_id = submissionId;
        };

        if (!Array.isArray(answer)) {
          processAnswer(answer);
        } else {
          answer.forEach(processAnswer);
        }
        this._http
          .put<IAnswer>(
            `${environment.irisApi}/workspace/${submissionId}/answer/`,
            answer,
          )
          .subscribe((response: IAnswer) => {
            if (Array.isArray(response)) {
              response.forEach((ans) => {
                this.updateAnswersAndThreads(ans);
              });
            } else {
              this.updateAnswersAndThreads(response);
            }
          });
      });
  }

  getQuestionRouteChoicesChoice(url: string) {
    const query = '';
    // TODO: Fix country API call
    const newURL = url
      .replace('${query}', query ?? '')
      .replace('${country_id}', query ?? '');
    const fullUrl = `${environment.irisApi}${newURL}`;
    return this._http.get<any>(fullUrl);
  }

  updateAnswersAndThreads(data: IAnswer) {
    const answer: IAnswerSchema = this._utils.convertKeysToCamelCase(data);
    this._store.dispatch(setAnswer({ answer }));
    this._store.dispatch(
      updateAnswerThroughSocket({
        answer: answer,
      }),
    );
    this._store.dispatch(closeActiveThread());
  }

  resubmitQuestionnaire(submissionId: string) {
    return this._http
      .put(
        `${environment.irisApi}/workspace/${submissionId}/complete-qc-answers/`,
        {},
      )
      .pipe(tap(() => this.reloadQuestionnaire()));
  }
}

/* eslint-disable @typescript-eslint/naming-convention*/

/* eslint-disable @typescript-eslint/naming-convention */
